import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport } from "ai";
import type { UIMessage } from "ai";
import { useLocation } from "react-router";
import {
  MessageCircle,
  X,
  Send,
  User,
  Loader2,
  ArrowRight,
  Sparkles,
  CheckCircle2,
} from "lucide-react";
import { useT } from "~/i18n/context";
import { cn } from "~/lib/utils";

function usePageContext() {
  const location = useLocation();
  const url = location.pathname;
  const designMatch = url.match(/\/(?:design|designs)\/([^/]+)/);
  return {
    url,
    designId: designMatch?.[1],
  };
}

function getTextFromMessage(msg: UIMessage): string {
  return msg.parts
    .filter((p): p is { type: "text"; text: string } => p.type === "text")
    .map((p) => p.text)
    .join("");
}

const SUGGESTION_PREFIX = ">> ";

function splitSuggestions(text: string): { body: string; suggestions: string[] } {
  const lines = text.split("\n");
  const suggestions: string[] = [];

  let i = lines.length - 1;
  while (i >= 0) {
    const trimmed = lines[i].trim();
    if (trimmed.startsWith(SUGGESTION_PREFIX)) {
      suggestions.unshift(trimmed.slice(SUGGESTION_PREFIX.length).trim());
      i--;
    } else if (trimmed === "") {
      i--;
    } else {
      break;
    }
  }

  const body = lines.slice(0, i + 1).join("\n").trimEnd();
  return { body, suggestions };
}

function hasToolResult(msg: UIMessage, toolName: string): boolean {
  return msg.parts.some(
    (p) => p.type === "tool-invocation" && "toolName" in p && p.toolName === toolName,
  );
}

function generateSessionId(): string {
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    const bytes = new Uint8Array(8);
    crypto.getRandomValues(bytes);
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
    return `ccs_${hex}`;
  }
  const hex = Math.random().toString(16).slice(2, 18).padEnd(16, "0");
  return `ccs_${hex}`;
}

type AdminMessage = { id: number; content: string; createdAt: string };

export function ConciergeChatWidget({ locale: localeProp }: { locale?: string }) {
  const { t, locale: contextLocale } = useT();
  const locale = localeProp ?? contextLocale;
  const [open, setOpen] = useState(false);
  const [handoffState, setHandoffState] = useState<"idle" | "connected">("idle");
  const [leadState, setLeadState] = useState<"idle" | "form" | "submitted">("idle");
  const [inputValue, setInputValue] = useState("");
  const [adminMessages, setAdminMessages] = useState<AdminMessage[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const pageContext = usePageContext();
  const sessionIdRef = useRef<string | null>(null);
  if (sessionIdRef.current === null && typeof window !== "undefined") {
    sessionIdRef.current = generateSessionId();
  }
  const lastAdminMsgIdRef = useRef<number>(0);
  const msgTimestamps = useRef<Map<string, number>>(new Map());

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: "/resources/concierge",
        prepareSendMessagesRequest: ({ messages: msgs }) => {
          const plainMessages = msgs.map((m) => ({
            role: m.role as "user" | "assistant",
            content: getTextFromMessage(m),
          }));
          return {
            body: {
              messages: plainMessages,
              sessionId: sessionIdRef.current ?? undefined,
              pageContext,
              locale,
            },
          };
        },
      }),
    [pageContext, locale],
  );

  const { messages, sendMessage, status } = useChat({
    transport,
    onFinish: ({ message }) => {
      if (hasToolResult(message, "request_human_handoff")) {
        setHandoffState("connected");
      }
      if (hasToolResult(message, "capture_lead")) {
        setLeadState("submitted");
      }

      const text = getTextFromMessage(message).toLowerCase();
      if (
        leadState === "idle" &&
        !hasToolResult(message, "capture_lead") &&
        (text.includes("caratzon team") ||
          text.includes("our team") ||
          text.includes("connect you") ||
          text.includes("your name and email") ||
          text.includes("your email"))
      ) {
        setLeadState("form");
      }
    },
  });

  const isStreaming = status === "streaming" || status === "submitted";

  useEffect(() => {
    const now = Date.now();
    for (const msg of messages) {
      if (!msgTimestamps.current.has(msg.id)) {
        msgTimestamps.current.set(msg.id, now);
      }
    }
  }, [messages]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }
  }, [messages, isStreaming, leadState, adminMessages.length]);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  useEffect(() => {
    function handleEscape(e: KeyboardEvent) {
      if (e.key === "Escape" && open) setOpen(false);
    }
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [open]);

  useEffect(() => {
    if (!open || messages.length === 0 || !sessionIdRef.current) return;
    const sid = sessionIdRef.current;
    const interval = setInterval(async () => {
      try {
        const params = new URLSearchParams({
          sessionId: sid,
          afterId: String(lastAdminMsgIdRef.current),
        });
        const res = await fetch(`/resources/concierge?${params}`);
        if (!res.ok) return;
        const data = await res.json();
        if (data.messages?.length > 0) {
          setAdminMessages((prev) => {
            const existingIds = new Set(prev.map((m) => m.id));
            const newMsgs = data.messages.filter(
              (m: AdminMessage) => !existingIds.has(m.id),
            );
            if (newMsgs.length === 0) return prev;
            const merged = [...prev, ...newMsgs];
            lastAdminMsgIdRef.current = Math.max(
              ...merged.map((m) => m.id),
            );
            return merged;
          });
        }
      } catch {
        // silently ignore polling errors
      }
    }, 8000);
    return () => clearInterval(interval);
  }, [open, messages.length]);

  const doSend = useCallback(
    (text: string) => {
      if (!text.trim() || isStreaming) return;
      setInputValue("");
      sendMessage({ text });
    },
    [isStreaming, sendMessage],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        doSend(inputValue);
      }
    },
    [inputValue, doSend],
  );

  const handleLeadSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const form = new FormData(e.currentTarget);
      const name = form.get("name") as string;
      const email = form.get("email") as string;
      const company = form.get("company") as string;
      const message = form.get("message") as string;
      if (!name || !email) return;
      setLeadState("submitted");
      const parts = [`My name is ${name}, email: ${email}`];
      if (company) parts.push(`company: ${company}`);
      if (message) parts.push(`Regarding: ${message}`);
      sendMessage({ text: parts.join(". ") });
    },
    [sendMessage],
  );

  const quickActions = t.concierge?.quickActions ?? [
    "How does licensing work?",
    "I'm a designer — how do I join?",
    "What are the pricing plans?",
    "I'd like to speak with someone",
  ];

  return (
    <>
      {/* Floating trigger */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={cn(
          "fixed bottom-6 right-6 z-[9999] flex items-center justify-center rounded-full shadow-lg transition-all duration-300",
          "hover:scale-105 active:scale-95",
          open
            ? "h-11 w-11 bg-foreground/10 backdrop-blur-sm text-foreground"
            : "h-14 w-14 bg-teal text-teal-fg",
        )}
        aria-label={open ? "Close chat" : "Open concierge chat"}
      >
        {open ? <X className="size-5" /> : <MessageCircle className="size-6" />}
      </button>

      {/* Chat panel */}
      <div
        className={cn(
          "fixed bottom-24 right-6 z-[9998] flex flex-col overflow-hidden rounded-2xl border border-border bg-background shadow-2xl transition-all duration-300",
          "w-[380px] max-w-[calc(100vw-3rem)]",
          open
            ? "h-[min(600px,calc(100vh-8rem))] opacity-100 translate-y-0 pointer-events-auto"
            : "h-0 opacity-0 translate-y-4 pointer-events-none",
        )}
      >
        {/* Header */}
        <div className="shrink-0 px-5 py-4 bg-teal text-teal-fg">
          <div className="flex items-center gap-3">
            <div className="relative flex h-9 w-9 items-center justify-center rounded-full bg-white/15">
              <MessageCircle className="size-4" />
              <span className="absolute -bottom-0.5 -right-0.5 flex h-3 w-3">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-400 border-2 border-teal" />
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium leading-tight">
                {t.concierge?.title ?? "Caratzon Concierge"}
              </p>
              <p className="text-[0.68rem] leading-tight opacity-70 mt-0.5">
                {t.concierge?.subtitle ?? "Your B2B jewelry sourcing assistant"}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="flex h-7 w-7 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              aria-label="Close chat"
            >
              <X className="size-3.5" />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto px-4 py-4 space-y-3 concierge-scroll"
        >
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-center px-4 gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-teal/10">
                <Sparkles className="size-5 text-teal" />
              </div>
              <div className="space-y-1.5">
                <p className="text-[0.82rem] font-medium text-foreground">
                  {t.concierge?.title ?? "Caratzon Concierge"}
                </p>
                <p className="text-[0.78rem] text-muted-foreground leading-relaxed max-w-[280px]">
                  {t.concierge?.welcome ??
                    "Welcome! Ask me about our designs, licensing tiers, manufacturing, or anything about Caratzon."}
                </p>
              </div>
              <div className="flex flex-wrap justify-center gap-2 mt-1">
                {quickActions.map((q: string) => (
                  <button
                    key={q}
                    type="button"
                    onClick={() => doSend(q)}
                    className="rounded-full border border-border px-3 py-1.5 text-[0.7rem] text-muted-foreground hover:border-teal/40 hover:text-foreground hover:bg-teal/5 transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {(() => {
            type MergedItem =
              | { kind: "chat"; msg: (typeof messages)[number]; idx: number }
              | { kind: "admin"; msg: AdminMessage };

            const merged: MergedItem[] = [
              ...messages.map((msg, idx) => ({ kind: "chat" as const, msg, idx })),
              ...adminMessages.map((msg) => ({ kind: "admin" as const, msg })),
            ];

            merged.sort((a, b) => {
              const aTime = a.kind === "chat"
                ? (msgTimestamps.current.get(a.msg.id) ?? 0)
                : new Date(a.msg.createdAt).getTime();
              const bTime = b.kind === "chat"
                ? (msgTimestamps.current.get(b.msg.id) ?? 0)
                : new Date(b.msg.createdAt).getTime();
              return aTime - bTime;
            });

            return merged.map((item, mergedIdx) => {
              if (item.kind === "admin") {
                const am = item.msg;
                return (
                  <div key={`admin-${am.id}`} className="concierge-msg-enter">
                    <div className="flex gap-2.5 justify-start">
                      <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-amber-500/20">
                        <User className="size-3 text-amber-500" />
                      </div>
                      <div className="max-w-[80%] rounded-2xl rounded-bl-md px-3.5 py-2.5 text-[0.8rem] leading-relaxed bg-amber-500/10 border border-amber-500/20 text-foreground">
                        <p className="text-[0.65rem] font-medium text-amber-500/80 mb-0.5">Caratzon Team</p>
                        <MessageContent content={am.content} />
                      </div>
                    </div>
                  </div>
                );
              }

              const { msg, idx } = item;
              const rawText = getTextFromMessage(msg);
              if (!rawText) return null;
              const isLastAssistant =
                msg.role === "assistant" &&
                idx === messages.length - 1 &&
                !isStreaming;
              const { body, suggestions } =
                msg.role === "assistant"
                  ? splitSuggestions(rawText)
                  : { body: rawText, suggestions: [] };
              const showSuggestions = isLastAssistant && suggestions.length > 0;

              return (
                <div key={msg.id} className="concierge-msg-enter" style={{ animationDelay: `${Math.min(mergedIdx * 30, 150)}ms` }}>
                  <div
                    className={cn(
                      "flex gap-2.5",
                      msg.role === "user" ? "justify-end" : "justify-start",
                    )}
                  >
                    {msg.role === "assistant" && (
                      <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-teal">
                        <MessageCircle className="size-3 text-white" />
                      </div>
                    )}
                    <div
                      className={cn(
                        "max-w-[80%] rounded-2xl px-3.5 py-2.5 text-[0.8rem] leading-relaxed",
                        msg.role === "user"
                          ? "bg-foreground text-background rounded-br-md"
                          : "bg-accent/60 text-foreground rounded-bl-md",
                      )}
                    >
                      <MessageContent content={body} />
                    </div>
                    {msg.role === "user" && (
                      <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-foreground/10">
                        <User className="size-3 text-foreground/60" />
                      </div>
                    )}
                  </div>
                  {showSuggestions && (
                    <div className="flex flex-wrap gap-1.5 mt-2 ml-8 concierge-msg-enter" style={{ animationDelay: "100ms" }}>
                      {suggestions.map((s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => doSend(s)}
                          className="rounded-full border border-border px-3 py-1.5 text-[0.7rem] text-muted-foreground hover:border-teal/40 hover:text-foreground hover:bg-teal/5 transition-colors"
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            });
          })()}

          {isStreaming && messages[messages.length - 1]?.role === "user" && (
            <div className="flex gap-2.5 justify-start concierge-msg-enter">
              <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-teal">
                <MessageCircle className="size-3 text-teal-fg" />
              </div>
              <div className="bg-accent/60 rounded-2xl rounded-bl-md px-4 py-3">
                <div className="flex gap-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-foreground/30 animate-bounce [animation-delay:0ms]" />
                  <span className="h-1.5 w-1.5 rounded-full bg-foreground/30 animate-bounce [animation-delay:150ms]" />
                  <span className="h-1.5 w-1.5 rounded-full bg-foreground/30 animate-bounce [animation-delay:300ms]" />
                </div>
              </div>
            </div>
          )}

          {/* Lead capture form */}
          {leadState === "form" && (
            <div className="mx-1 rounded-xl border border-border bg-accent/30 p-4 concierge-msg-enter">
              <p className="text-[0.78rem] font-medium text-foreground mb-1">
                {t.concierge?.leadForm?.title ?? "Connect with our team"}
              </p>
              <p className="text-[0.68rem] text-muted-foreground mb-3">
                {t.concierge?.leadForm?.description ??
                  "Share your details and our team will follow up within 24 hours."}
              </p>
              <form onSubmit={handleLeadSubmit} className="space-y-2.5">
                <input
                  name="name"
                  required
                  placeholder={t.concierge?.leadForm?.name ?? "Your name"}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-[0.78rem] placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-teal/40"
                />
                <input
                  name="email"
                  type="email"
                  required
                  placeholder={t.concierge?.leadForm?.email ?? "Business email"}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-[0.78rem] placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-teal/40"
                />
                <input
                  name="company"
                  placeholder={t.concierge?.leadForm?.company ?? "Company (optional)"}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-[0.78rem] placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-teal/40"
                />
                <textarea
                  name="message"
                  rows={2}
                  placeholder={t.concierge?.leadForm?.message ?? "Briefly describe what you need help with..."}
                  className="w-full resize-none rounded-lg border border-border bg-background px-3 py-2 text-[0.78rem] leading-relaxed placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-teal/40"
                />
                <button
                  type="submit"
                  className="w-full flex items-center justify-center gap-2 rounded-lg bg-teal text-teal-fg px-4 py-2.5 text-[0.78rem] font-medium transition-colors hover:bg-teal-hover"
                >
                  {t.concierge?.leadForm?.submit ?? "Connect me with the team"}
                  <ArrowRight className="size-3.5" />
                </button>
              </form>
            </div>
          )}

          {leadState === "submitted" && (
            <div className="mx-1 rounded-xl border border-teal/20 bg-teal/5 p-4 concierge-msg-enter">
              <div className="flex items-start gap-2.5">
                <CheckCircle2 className="size-4 text-teal shrink-0 mt-0.5" />
                <div>
                  <p className="text-[0.78rem] font-medium text-foreground">
                    {t.concierge?.leadForm?.successTitle ?? "You're connected"}
                  </p>
                  <p className="text-[0.7rem] text-muted-foreground mt-0.5">
                    {t.concierge?.leadForm?.success ??
                      "Our team will reach out within 24 hours. You can continue chatting here in the meantime."}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Handoff confirmation */}
          {handoffState === "connected" && (
            <div className="mx-1 rounded-xl border border-teal/20 bg-teal/5 p-4 concierge-msg-enter">
              <div className="flex items-start gap-2.5">
                <User className="size-4 text-teal shrink-0 mt-0.5" />
                <div>
                  <p className="text-[0.78rem] font-medium text-foreground">
                    {t.concierge?.handoffConfirm?.title ?? "Handoff requested"}
                  </p>
                  <p className="text-[0.7rem] text-muted-foreground mt-0.5">
                    {t.concierge?.handoffConfirm?.description ??
                      "We've notified our team with your request details. A team member will be in touch shortly. You can continue chatting here or reach us directly at trade@caratzon.com."}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <div className="shrink-0 border-t border-border bg-background px-3 py-3">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              doSend(inputValue);
            }}
            className="flex items-end gap-2"
          >
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t.concierge?.inputPlaceholder ?? "Ask about designs, licensing, pricing..."}
              rows={1}
              className="flex-1 resize-none rounded-xl border border-border bg-accent/30 px-3.5 py-2.5 text-[0.8rem] leading-relaxed placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-teal/40 max-h-24"
              style={{ fieldSizing: "content" } as React.CSSProperties}
            />
            <button
              type="submit"
              disabled={!inputValue.trim() || isStreaming}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-teal text-teal-fg transition-all hover:bg-teal-hover disabled:opacity-40"
            >
              {isStreaming ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Send className="size-4" />
              )}
            </button>
          </form>
          <p className="mt-2 text-center text-[0.6rem] text-muted-foreground/40">
            {t.concierge?.disclaimer ?? "AI-powered. Responses may not be fully accurate."}
          </p>
        </div>
      </div>
    </>
  );
}

function MessageContent({ content }: { content: string }) {
  const lines = content.split("\n");
  const elements: React.ReactNode[] = [];
  let listBuffer: string[] = [];
  let key = 0;

  function flushList() {
    if (listBuffer.length === 0) return;
    elements.push(
      <ul key={key++} className="my-1 ml-3 space-y-0.5 list-disc list-outside marker:text-teal/50">
        {listBuffer.map((item, i) => (
          <li key={i} className="pl-1">{renderInline(item)}</li>
        ))}
      </ul>,
    );
    listBuffer = [];
  }

  for (const line of lines) {
    const bulletMatch = line.match(/^[\s]*[-•*]\s+(.+)/);
    const numberedMatch = line.match(/^[\s]*\d+[.)]\s+(.+)/);

    if (bulletMatch) {
      listBuffer.push(bulletMatch[1]);
    } else if (numberedMatch) {
      listBuffer.push(numberedMatch[1]);
    } else {
      flushList();
      if (line.trim() === "") {
        elements.push(<span key={key++} className="block h-1.5" />);
      } else {
        elements.push(
          <span key={key++} className="block">
            {renderInline(line)}
          </span>,
        );
      }
    }
  }
  flushList();

  return <>{elements}</>;
}

function renderInline(text: string) {
  const tokens = text.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g);
  return tokens.map((token, i) => {
    if (token.startsWith("**") && token.endsWith("**")) {
      return <strong key={i}>{token.slice(2, -2)}</strong>;
    }
    const linkMatch = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (linkMatch) {
      return (
        <a
          key={i}
          href={linkMatch[2]}
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-2 decoration-teal/40 hover:decoration-teal transition-colors"
        >
          {linkMatch[1]}
        </a>
      );
    }
    return <span key={i}>{token}</span>;
  });
}
